import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useGetUserLeagues } from '../api/leaguesApi';
import { useQuickplayState } from '../api/quickplayApi';
import {
  buildOAuthCallbackUrl,
  resolveLoginRedirectTarget,
  resolvePostAuthDestination
} from '../components/routing/loginRedirect';
import { Spinner } from '../components/common/Spinner';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabaseClient';
import posthog from 'posthog-js';

const DEFAULT_OAUTH_ERROR_MESSAGE = 'Social sign-in could not be completed. Please try again.';
const EXISTING_ACCOUNT_NOTICE = 'This account already has a Nova profile - signing you in.';
const POST_AUTH_NAVIGATION_TIMEOUT_MS = 8000;

function getOAuthCallbackState(searchParams, hash) {
  const hashParams = new URLSearchParams(String(hash || '').replace(/^#/, ''));
  const errorCodes = [
    searchParams.get('error_code'),
    hashParams.get('error_code')
  ].filter(Boolean);
  const errorMessages = [
    searchParams.get('error'),
    searchParams.get('error_description'),
    hashParams.get('error'),
    hashParams.get('error_description')
  ].filter(Boolean);

  return {
    errorMessage: getOAuthCallbackErrorMessage(searchParams, hashParams),
    isIdentityAlreadyLinked: (
      errorCodes.some((code) => code.toLowerCase() === 'identity_already_exists') ||
      errorMessages.some((message) => message.toLowerCase().includes('already linked'))
    ),
    provider: searchParams.get('provider') || hashParams.get('provider') || 'google'
  };
}

function getOAuthCallbackErrorMessage(searchParams, hashParams) {
  const queryError = searchParams.get('error_description') || searchParams.get('error');
  if (queryError) {
    return formatOAuthCallbackError(queryError);
  }

  const hashError = hashParams.get('error_description') || hashParams.get('error');
  return hashError ? formatOAuthCallbackError(hashError) : '';
}

function formatOAuthCallbackError(rawError) {
  const message = String(rawError || '').trim();
  if (!message) return DEFAULT_OAUTH_ERROR_MESSAGE;
  if (message.toLowerCase() === 'access_denied') {
    return 'Sign-in was cancelled. Please try again.';
  }
  return `Social sign-in could not be completed: ${message}`;
}

function AuthCallback() {
  const { ensureUserProfile, user } = useAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const ensureUserProfileRef = useRef(ensureUserProfile);
  const hasNavigatedRef = useRef(false);
  const navigationContextRef = useRef({
    userLeagues: null,
    redirectUser: null,
    explicitRedirectTo: null,
    isGuestUpgrade: false,
    quickplayState: null
  });
  const [readyForRedirect, setReadyForRedirect] = useState(false);
  const [ensuredUser, setEnsuredUser] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const explicitRedirectTo = resolveLoginRedirectTarget(searchParams.get('redirect'));
  const isGuestUpgrade = searchParams.get('signup') === 'guest_upgrade';
  const {
    errorMessage: oauthCallbackError,
    isIdentityAlreadyLinked,
    provider: oauthProvider
  } = getOAuthCallbackState(searchParams, location.hash);
  const redirectUser = user || ensuredUser;
  const shouldResolveDestination = readyForRedirect && !!redirectUser;
  const { data: userLeagues, isLoading: leaguesLoading } = useGetUserLeagues(
    shouldResolveDestination
  );
  const shouldLoadQuickplayState = Boolean(
    shouldResolveDestination &&
    isGuestUpgrade
  );
  const {
    data: quickplayState,
    isLoading: quickplayStateLoading
  } = useQuickplayState(shouldLoadQuickplayState);

  useEffect(() => {
    ensureUserProfileRef.current = ensureUserProfile;
  }, [ensureUserProfile]);

  useEffect(() => {
    navigationContextRef.current = {
      userLeagues,
      redirectUser,
      explicitRedirectTo,
      isGuestUpgrade,
      quickplayState
    };
  }, [redirectUser, userLeagues, explicitRedirectTo, isGuestUpgrade, quickplayState]);

  // NOTE: no one-shot guard here. Under React StrictMode the mount runs twice:
  // the cleanup cancels only the stale run, and the surviving run completes.
  // ensure-profile is idempotent server-side, so a duplicate call is harmless;
  // a guard that blocks the re-run deadlocks the page (result discarded as
  // cancelled, second run never starts) and must not be reintroduced.
  useEffect(() => {
    let active = true;

    if (isIdentityAlreadyLinked) {
      setNotice(EXISTING_ACCOUNT_NOTICE);

      const signInToExistingAccount = async () => {
        try {
          const { error: signInError } = await supabase.auth.signInWithOAuth({
            provider: oauthProvider,
            options: {
              redirectTo: buildOAuthCallbackUrl(
                explicitRedirectTo,
                isGuestUpgrade ? { signup: 'guest_upgrade' } : {}
              )
            }
          });

          if (active && signInError) {
            setError(
              signInError.message ||
              DEFAULT_OAUTH_ERROR_MESSAGE
            );
          }
        } catch (err) {
          if (active) {
            setError(err?.message || DEFAULT_OAUTH_ERROR_MESSAGE);
          }
        }
      };

      signInToExistingAccount();

      return () => {
        active = false;
      };
    }

    if (oauthCallbackError) {
      setError(oauthCallbackError);
      return undefined;
    }

    const finishSignIn = async () => {
      try {
        const profile = await ensureUserProfileRef.current();
        if (active) {
          if (isGuestUpgrade && profile?.isGuest === false) {
            posthog.capture('signup_completed', {
              method: 'linked_identity'
            });
          }
          setEnsuredUser(profile);
          setReadyForRedirect(true);
        }
      } catch (err) {
        if (active) {
          setError(err?.message || 'Nova could not finish signing you in. Please try again.');
        }
      }
    };

    finishSignIn();

    return () => {
      active = false;
    };
  }, [
    explicitRedirectTo,
    isGuestUpgrade,
    isIdentityAlreadyLinked,
    oauthCallbackError,
    oauthProvider
  ]);

  useEffect(() => {
    if (!readyForRedirect || error) {
      return;
    }

    if (leaguesLoading || (shouldLoadQuickplayState && quickplayStateLoading)) {
      return;
    }

    if (!redirectUser) {
      return;
    }

    const destination = resolvePostAuthDestination({
      redirectTarget: explicitRedirectTo,
      userLeagues,
      user: redirectUser,
      isGuestUpgrade,
      quickplayState
    });
    hasNavigatedRef.current = true;
    navigate(destination, { replace: true });
  }, [
    readyForRedirect,
    error,
    explicitRedirectTo,
    isGuestUpgrade,
    leaguesLoading,
    quickplayState,
    quickplayStateLoading,
    shouldLoadQuickplayState,
    userLeagues,
    redirectUser,
    navigate
  ]);

  // Watchdog: armed from mount, regardless of how far the flow above got.
  // Guarantees a terminal state - either a fallback navigation (profile known)
  // or a visible error with a way out - so the spinner can never be permanent.
  // Skipped only while an identity-collision sign-in redirect is leaving the page.
  useEffect(() => {
    if (isIdentityAlreadyLinked) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      if (hasNavigatedRef.current) {
        return;
      }

      const {
        userLeagues: latestUserLeagues,
        redirectUser: latestRedirectUser,
        explicitRedirectTo: latestExplicitRedirectTo,
        isGuestUpgrade: latestIsGuestUpgrade,
        quickplayState: latestQuickplayState
      } = navigationContextRef.current;

      if (latestRedirectUser) {
        hasNavigatedRef.current = true;
        // Same resolver as the happy path above, so a saved redirect target
        // (e.g. an invite link) survives even when the fallback fires.
        navigate(
          resolvePostAuthDestination({
            redirectTarget: latestExplicitRedirectTo,
            userLeagues: latestUserLeagues,
            user: latestRedirectUser,
            isGuestUpgrade: latestIsGuestUpgrade,
            quickplayState: latestQuickplayState
          }),
          { replace: true }
        );
        return;
      }

      setError((previousError) => (
        previousError || 'Nova could not finish signing you in. Please try again.'
      ));
    }, POST_AUTH_NAVIGATION_TIMEOUT_MS);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isIdentityAlreadyLinked, navigate]);

  return (
    <div className="page-login">
      <div className="wrapper">
        <h1>LOG IN</h1>
        <div className="form-block">
          {error ? (
            <>
              <div className="error-message" style={{ color: 'red', marginBottom: '1rem' }}>
                {error}
              </div>
              <Link to="/login" className="submit-btn">
                BACK TO LOGIN
              </Link>
            </>
          ) : (
            <>
              {notice ? (
                <div role="status" style={{ marginBottom: '1rem' }}>
                  {notice}
                </div>
              ) : null}
              <div className="loader" style={{ display: 'flex', justifyContent: 'center', padding: '2rem 0' }}>
                <Spinner />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export { AuthCallback };
